<template>
  <v-card outlined>
    <v-card-title class="d-flex align-center">
      <span class="headline">OEE por máquina</span>
      <v-spacer />
      <v-chip small class="mx-1" color="blue lighten-5" text-color="blue darken-2">
        {{ maquinasUnicas.length }} máquina(s)
      </v-chip>
    </v-card-title>

    <v-card-text>
      <apexchart
        type="bar"
        height="260"
        :options="chartOptions"
        :series="chartSeries"
      />
    </v-card-text>
  </v-card>
</template>

<script>
/*
  OeeBarPorMaquina.vue
  — Calcula el OEE por máquina usando EXCLUSIVAMENTE el valor 'oee' de cada fila.
  — Promedia por máquina sobre el periodo mostrado.
  — Por defecto incluye los días con 0.00% (se puede desactivar con la prop incluirCeros).
*/
export default {
  name: 'OeeBarPorMaquina',
  props: {
    // Filas normalizadas que envía el padre (gfData)
    // Espera campos: { label (nombre máquina), key, fecha, oee, teoricas? }
    rows: { type: Array, default: () => [] },

    // Si true (default), los días con 0.00% cuentan en el promedio.
    // Si false, se excluyen de la media.
    incluirCeros: { type: Boolean, default: true },

    // Decimales para mostrar
    dec: { type: Number, default: 0 }
  },

  computed: {
    // Lista única de máquinas encontrada en rows
    maquinasUnicas () {
      const set = new Map()
      for (const r of (this.rows || [])) {
        const id = String(r.key ?? r.id ?? r.label ?? '—')
        const label = String(r.label ?? r.nombre ?? r.maquina ?? id)
        if (!set.has(id)) set.set(id, label)
      }
      return Array.from(set.entries()).map(([key, label]) => ({ key, label }))
    },

    // Promedio de OEE por máquina usando SOLO el campo 'oee' ya provisto
    oeePorMaquina () {
      const acc = new Map() // id -> { label, sum, n }
      for (const r of (this.rows || [])) {
        const id = String(r.key ?? r.id ?? r.label ?? '—')
        const label = String(r.label ?? r.nombre ?? r.maquina ?? id)
        const val = Number(r.oee ?? r.OEE ?? 0)

        // Excluir ceros si así se pide
        if (!this.incluirCeros && val === 0) continue

        if (!acc.has(id)) acc.set(id, { label, sum: 0, n: 0 })
        const a = acc.get(id)
        a.sum += val
        a.n   += 1
      }

      // Construye arreglo [{label, value}]
      const out = []
      for (const { key, label } of this.maquinasUnicas) {
        const a = acc.get(String(key))
        const v = a && a.n ? (a.sum / a.n) : 0
        out.push({ label, value: Number.isFinite(v) ? v : 0 })
      }

      // Ordena desc por OEE
      out.sort((a, b) => b.value - a.value)
      return out
    },

    chartSeries () {
      return [{
        name: 'OEE',
        data: this.oeePorMaquina.map(x => Number(x.value || 0))
      }]
    },

    chartOptions () {
      return {
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: { horizontal: true, barHeight: '60%', borderRadius: 4 }
        },
        dataLabels: {
          enabled: true,
          formatter: (v) => `${Math.floor(Number(v || 0))}%`
        },
        xaxis: {
          categories: this.oeePorMaquina.map(x => x.label),
          max: 100,
          labels: {
            formatter: (v) => `${Math.floor(Number(v || 0))}%`
          }
        },
        yaxis: {
          labels: { maxWidth: 120 }
        },
        grid: { strokeDashArray: 4 },
        colors: ['#26A69A'],
        tooltip: {
          y: {
            formatter: (v) => `${Math.floor(Number(v || 0))}%`
          }
        }
      }
    }
  }
}
</script>
